import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './index.css';
/* Canonical design primitives (brand, text, spacing, radius, motion, z).
   Loaded before components so every namespace can reference the shared tokens. */
import './ComponentsStyling/tokens.css';
import App from './App';
import ScrollControls from './components/ScrollControls';
import reportWebVitals from './reportWebVitals';
/* Loaded last on purpose: site-wide readability/usability layer that lifts
   text contrast, border visibility, focus rings and motion — must win the
   cascade over the per-component styles imported above. */
import './ComponentsStyling/GlobalUX.css';
/* Cross-cutting performance & interaction polish (tap feel, overflow safety,
   reduced-motion honour). Global smoothness only — no visual identity here. */
import './ComponentsStyling/smooth.css';

/* Each secondary page is its own lazily-loaded chunk. The thunks live in one
   map so we can both (a) hand them to React.lazy for code-splitting and
   (b) fire them during idle time to PREFETCH every page in the background —
   so by the time a user taps any nav item, the page is already downloaded and
   navigation feels instant. First load stays light (only the landing page);
   subsequent navigation is effectively free. */
const loaders = {
  about: () => import('./pages/About'),
  agenda: () => import('./pages/Agenda'),
  competition: () => import('./pages/Competition'),
  expoday: () => import('./pages/ExpoDay'),
  faq: () => import('./pages/FAQ'),
  judges: () => import('./pages/Judges'),
  speakers: () => import('./pages/Speakers'),
  workshops: () => import('./pages/Workshops'),
  sponsors: () => import('./pages/SponsorsPage'),
  contact: () => import('./pages/Contact'),
};

const About = lazy(loaders.about);
const Agenda = lazy(loaders.agenda);
const Competition = lazy(loaders.competition);
const ExpoDay = lazy(loaders.expoday);
const FAQ = lazy(loaders.faq);
const Judges = lazy(loaders.judges);
const Speakers = lazy(loaders.speakers);
const Workshops = lazy(loaders.workshops);
const SponsorsPage = lazy(loaders.sponsors);
const Contact = lazy(loaders.contact);

const RouteFallback = () => (
  <div className="ux-route-loading" aria-label="Loading" role="status">
    <span className="ux-route-loading__spinner" />
  </div>
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Router>
      <ScrollControls />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<App />} />
          <Route path="/about" element={<About />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/program" element={<Competition />} />
          <Route path="/competition" element={<Competition />} />
          <Route path="/exhibit" element={<ExpoDay />} />
          <Route path="/expoday" element={<ExpoDay />} />
          <Route path="/expo-day" element={<ExpoDay />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/judges" element={<Judges />} />
          <Route path="/speakers" element={<Speakers />} />
          <Route path="/workshops" element={<Workshops />} />
          <Route path="/partners" element={<SponsorsPage />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </Suspense>
    </Router>
  </React.StrictMode>
);

/* Warm the cache: prefetch all page chunks once the browser is idle, so
   navigation after first paint is instant. Falls back to a timeout where
   requestIdleCallback is unsupported (Safari). */
const prefetchAll = () => Object.values(loaders).forEach((load) => load());
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(prefetchAll, { timeout: 4000 });
  } else {
    window.setTimeout(prefetchAll, 2500);
  }
}

/* Web performance tracking. Uncomment to enable analytics. */
reportWebVitals();
